import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Segmented } from 'antd';
import { usePaginatedList } from '../hooks/usePaginatedList';
import { useCountingRM } from '../hooks/useCountUp';
import { Chip, EmptyState, InfiniteSentinel, Skeletons, RM, RM0, RMk } from '../components/kit';
import PeriodPicker, { presetRange } from '../components/PeriodPicker';
import NotifBell from '../components/NotifBell';
import CommissionSheet from '../components/CommissionSheet';
import PayoutSheet, { VoucherRef, PdfMark } from '../components/PayoutSheet';
import Erp213VoucherSheet from '../components/Erp213VoucherSheet';
import EarningsMonthly from '../components/EarningsMonthly';
import { shortAddress } from '../lib/format';
import { stateLine } from '../lib/commission';
import { isReversed, noVoucherLine, payoutDate, payoutNet } from '../lib/payout';
import * as earnApi from '../api/earnings';

// Build the /earnings/statement query string for a { from, to } period. The header
// button is the only route to the statement — the old list of past statements below
// the ledger duplicated what that page already shows for the selected period.
const statementHref = (p) => {
  const qs = new URLSearchParams();
  if (p?.from) qs.set('from', p.from);
  if (p?.to) qs.set('to', p.to);
  const s = qs.toString();
  return `/earnings/statement${s ? `?${s}` : ''}`;
};

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' }}>{children}</div>
);
/* Counts to its figure rather than snapping. useCountingRM travels from whatever
   is on screen to the new number, so money moving between these tiles reads as
   one movement: when a payout lands, PENDING walks down while PAID walks up. */
const Tile = ({ label, amount, color }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '12px 12px 10px' }}>
    <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600, letterSpacing: '.05em' }}>{label}</div>
    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 3, color: color || 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
      {useCountingRM(amount)}
    </div>
  </div>
);

export default function EarningsPage() {
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [seg, setSeg] = useState('By sale');
  // The by-sale row whose commission split is open in the bottom sheet, or null.
  const [openDeal, setOpenDeal] = useState(null);
  // Selected period ({ from, to } | null). Default: this year (matches the YTD hero).
  const [period, setPeriod] = useState(() => presetRange('This year'));
  const [periodSummary, setPeriodSummary] = useState(null);
  /* The open payout lives in the URL, so a "Payout paid" / "Payment voucher
     ready" notification can deep-link straight to its sheet (/earnings?payout=)
     — and a tap that lands while Earnings is already open still opens it, since
     only the search changes. Opening and closing REPLACE the entry: the sheet is
     a view of this screen, not a step back has to walk through. */
  const [sp, setSp] = useSearchParams();
  const openPayoutId = sp.get('payout');
  const setOpenPayout = (id) => {
    const next = new URLSearchParams(sp);
    if (id) next.set('payout', id); else next.delete('payout');
    setSp(next, { replace: true });
  };

  // Params passed to every period-aware list/summary call.
  const periodParams = useMemo(
    () => ({ ...(period?.from ? { from: period.from } : {}), ...(period?.to ? { to: period.to } : {}) }),
    [period]
  );

  useEffect(() => {
    earnApi.listEarningsSummary().then((r) => setS(r?.data ?? r)).catch(() => {});
    earnApi.listEarningsLedger().then((r) => setLedger(r?.data ?? r)).catch(() => {});
  }, []);

  // Period-scoped tiles reload whenever the period changes.
  useEffect(() => {
    setPeriodSummary(null);
    earnApi.getEarningsPeriodSummary(periodParams).then((r) => setPeriodSummary(r?.data ?? r)).catch(() => setPeriodSummary(null));
  }, [periodParams]);

  /* Re-read the figures in place, WITHOUT blanking them first: the tiles animate
     from the number already on screen, so clearing them would restart the count
     from zero and a figure that fell — PENDING emptying into PAID — would show no
     movement at all. Fired when money moves behind the agent's back. */
  useEffect(() => {
    const onRefresh = () => {
      earnApi.listEarningsSummary().then((r) => setS(r?.data ?? r)).catch(() => {});
      earnApi.listEarningsLedger().then((r) => setLedger(r?.data ?? r)).catch(() => {});
      earnApi.getEarningsPeriodSummary(periodParams).then((r) => setPeriodSummary(r?.data ?? r)).catch(() => {});
      bySale.refresh?.();
      overrides.refresh?.();
      payouts.refresh?.();
    };
    window.addEventListener('cid:refresh', onRefresh);
    return () => window.removeEventListener('cid:refresh', onRefresh);
  }); // no dep array: the list hooks hand back fresh closures every render

  const bySale = usePaginatedList(earnApi.listEarningsBySale, { limit: 10, params: periodParams, enabled: seg === 'By sale' });
  const overrides = usePaginatedList(earnApi.listEarningsOverrides, { limit: 10, params: periodParams, enabled: seg === 'Overrides' });
  // Every payout, all time — like the ledger above it, not the period.
  const payouts = usePaginatedList(earnApi.listEarningsPayouts, { limit: 10 });
  // Payment vouchers from ERP213, before CIDAS. Most agents who joined after the
  // switch have none, so the section only shows once there is something in it.
  const erpVouchers = usePaginatedList(earnApi.listErp213Vouchers, { limit: 10 });
  const [erpSummary, setErpSummary] = useState(null);
  useEffect(() => {
    let live = true;
    earnApi.listErp213Vouchers({ page: 1, limit: 1 })
      .then((r) => { if (live) setErpSummary(r?.summary || null); })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  const [openErpId, setOpenErpId] = useState(null);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '15px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div style={{ flex: 1, fontSize: 17, fontWeight: 600 }}>Earnings</div>
        <button data-tour="earnings-statement-btn" onClick={() => nav(statementHref(period))} style={exportBtn}>Statement ›</button>
        <NotifBell />
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!s ? (
          <Skeletons n={2} height={92} />
        ) : (
          /* hero — YTD / lifetime GCI. Both are compact (RMk): the width of a compact
             figure is bounded, so two of them fit side by side at any magnitude on
             the narrowest phone — which is what lets the type be 24px rather than 20.
             At full precision "RM 1,250,000.00" is wider than its column, and the
             only break opportunity is the space after "RM", so the currency orphans
             onto its own line and the number drops beneath it. */
          <div style={{ background: 'linear-gradient(135deg,#241854,#18345E)', borderRadius: 16, padding: 18, color: '#fff', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14 }}>
              <div>
                <div style={heroLabel}>YTD GCI</div>
                <div style={heroValue}>{RMk(s.ytdGci)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={heroLabel}>LIFETIME GCI</div>
                <div style={heroValue}>{RMk(s.lifetimeGci)}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 11.5, opacity: 0.82 }}>
                {s.nextTier != null ? `${s.tier}% tier · ${RMk(s.toNextTier)} to ${s.nextTier}%` : `${s.tier}% tier · top of the ladder`}
              </span>
              <a onClick={() => nav('/earnings/tier-ladder')} style={{ fontSize: 11.5, fontWeight: 600, color: '#9DC6F2', cursor: 'pointer', whiteSpace: 'nowrap' }}>Tier ladder ›</a>
            </div>
          </div>
        )}

        {/* period selector + period-scoped tiles */}
        <SectionLabel>PERIOD</SectionLabel>
        <div style={{ marginTop: -4, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <PeriodPicker value={period} onChange={setPeriod} />
          {!periodSummary ? (
            <Skeletons n={1} height={72} />
          ) : (
            <>
              <div data-tour="earnings-period-tiles" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <Tile label="EARNED" amount={periodSummary.earned} />
                <Tile label="PAID" amount={periodSummary.paid} color="var(--ok)" />
                <Tile label="PENDING" amount={periodSummary.pending} color="var(--warn)" />
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', padding: '0 2px' }}>
                {periodSummary.deals} deal{periodSummary.deals === 1 ? '' : 's'}
                {periodSummary.override > 0 ? ` · override ${RM0(periodSummary.override)}` : ''}
              </div>
            </>
          )}
        </div>

        {/* the year at a glance, month by month. Sits under the period tiles, not
            above them: the tiles answer "what is my money doing right now", which
            is what an agent opens this tab for; the year is the wider read. It
            carries its own year, deliberately — a monthly breakdown of "All time"
            or of a fortnight is not a thing anyone reads. */}
        <SectionLabel>TOTAL EARNING</SectionLabel>
        <div style={{ marginTop: -4 }}>
          <EarningsMonthly />
        </div>

        {/* per-sale breakdown / overrides */}
        <SectionLabel>{seg === 'By sale' ? 'MY DEALS' : 'OVERRIDE INCOME · FROM MY TEAM'}</SectionLabel>
        <Segmented block value={seg} onChange={setSeg} options={['By sale', 'Overrides']} style={{ marginTop: -4 }} />

        {seg === 'By sale' ? (
          bySale.loading ? (
            <Skeletons n={3} />
          ) : bySale.empty ? (
            <EmptyState title="No commission lines yet" sub="Your per-sale earnings appear here once a sale is booked." />
          ) : (
            <>
              {bySale.items.map((r) => (
                <div
                  key={r.id}
                  onClick={() => setOpenDeal(r)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenDeal(r); } }}
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 8, cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, fontFamily: 'ui-monospace,monospace' }}>{r.sale_ref}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{RM0(r.entitled)}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {shortAddress(r.property)}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 11.5, color: 'var(--muted)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stateLine(r)}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                      <Chip label={r.chip} />
                      <span style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1 }}>›</span>
                    </span>
                  </div>
                </div>
              ))}
              <InfiniteSentinel hasMore={bySale.hasMore} loading={bySale.loadingMore} onReach={bySale.loadMore} />
            </>
          )
        ) : overrides.loading ? (
          <Skeletons n={3} />
        ) : overrides.empty ? (
          <EmptyState title="No override income yet" sub="Override earnings from your downline deals appear here." />
        ) : (
          <>
            {overrides.items.map((o) => (
              <div key={o.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '15px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{o.from} · Gen {o.gen}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)', fontFamily: 'ui-monospace,monospace' }}>{o.sale_ref}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{RM0(o.amount)}</div>
              </div>
            ))}
            <InfiniteSentinel hasMore={overrides.hasMore} loading={overrides.loadingMore} onReach={overrides.loadMore} />
          </>
        )}

        {/* payout ledger — the running balance HQ owes. Cents stay here: this is the
            figure an agent reconciles against a statement. */}
        <SectionLabel>PAYOUT LEDGER</SectionLabel>
        {!ledger ? (
          <Skeletons n={1} height={120} />
        ) : (
          <div className="kv-card" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', marginTop: -4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 16px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 13, color: 'var(--muted)' }}>Balance</span>
              <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{RM(ledger.remaining_balance)}</span>
                <Chip label={ledger.status} />
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span>Total paid</span>
              <span style={{ fontWeight: 600, color: 'var(--ok)', fontVariantNumeric: 'tabular-nums' }}>{RM(ledger.total_paid)}</span>
            </div>
            {Number(ledger.total_clawback) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', fontSize: 13 }}>
                <span>Total clawback</span>
                <span style={{ fontWeight: 600, color: 'var(--bad)', fontVariantNumeric: 'tabular-nums' }}>−{RM(ledger.total_clawback)}</span>
              </div>
            )}
          </div>
        )}
        {/* every payout HQ made, with the payment voucher behind it. Under the
            ledger because these are the movements that ledger's "Total paid"
            adds up. A failed read says so — an empty list would tell the agent
            HQ never paid them. */}
        <SectionLabel>PAYOUTS{payouts.pagination?.total ? ` · ${payouts.pagination.total}` : ''}</SectionLabel>
        {payouts.loading ? (
          <Skeletons n={2} height={62} />
        ) : payouts.error && payouts.items.length === 0 ? (
          <div style={{ ...listCard, padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Couldn&rsquo;t load your payouts.</span>
            <Button size="small" onClick={payouts.refresh}>Try again</Button>
          </div>
        ) : payouts.empty ? (
          <div style={{ ...listCard, padding: '14px 16px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            No payouts yet. Each one HQ pays you appears here, with its payment voucher.
          </div>
        ) : (
          <>
            <div data-tour="earnings-payouts" style={listCard}>
              {payouts.items.map((p, i) => {
                const rev = isReversed(p);
                return (
                  <div
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpenPayout(p.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenPayout(p.id); } }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: i < payouts.items.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{payoutDate(p.payout_date)}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2, display: 'flex', minWidth: 0 }}>
                        {p.voucher?.doc_no ? <VoucherRef voucher={p.voucher} /> : <span>{noVoucherLine(p)}</span>}
                      </div>
                    </div>
                    {rev && <Chip label="Reversed" tone="bad" style={{ flex: 'none' }} />}
                    <span style={{ fontSize: 13.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: rev ? 'var(--muted)' : 'var(--ok)', textDecoration: rev ? 'line-through' : 'none' }}>
                      {RM(payoutNet(p))}
                    </span>
                    <span style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1 }}>›</span>
                  </div>
                );
              })}
            </div>
            {payouts.hasMore && (
              <Button block loading={payouts.loadingMore} onClick={payouts.loadMore} style={{ marginTop: -4 }}>Show more payouts</Button>
            )}
          </>
        )}
        {/* Older payments: the agent's vouchers from ERP213, the system before
            CIDAS. History only — their amounts are already in Paid above. */}
        {erpVouchers.items.length > 0 && (
          <>
            <SectionLabel>OLDER PAYMENTS · ERP213{erpSummary?.count ? ` · ${erpSummary.count}` : ''}</SectionLabel>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, marginTop: -6 }}>
              Paid before CIDAS{erpSummary?.total_amount != null ? `, ${RM(erpSummary.total_amount)} in all` : ''}. Already counted in Paid.
            </div>
            <div data-tour="earnings-erp213" style={listCard}>
              {erpVouchers.items.map((v, i) => (
                <div
                  key={v.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenErpId(v.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenErpId(v.id); } }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: i < erpVouchers.items.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{payoutDate(v.pv_date)}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      {v.has_file && <PdfMark />}
                      <span style={{ fontFamily: 'ui-monospace,monospace', whiteSpace: 'nowrap' }}>{v.pv_no}</span>
                      {v.sales?.length > 0 && (
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>· {v.sales.map((x) => x.sale_ref).join(', ')}</span>
                      )}
                    </div>
                  </div>
                  <span style={{ fontSize: 13.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{RM(v.amount)}</span>
                  <span style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1 }}>›</span>
                </div>
              ))}
            </div>
            {erpVouchers.hasMore && (
              <Button block loading={erpVouchers.loadingMore} onClick={erpVouchers.loadMore} style={{ marginTop: -4 }}>Show more</Button>
            )}
          </>
        )}
        <div style={{ height: 6 }} />
      </div>

      <CommissionSheet open={!!openDeal} deal={openDeal} onClose={() => setOpenDeal(null)} />
      <PayoutSheet
        open={!!openPayoutId}
        payoutId={openPayoutId}
        row={payouts.items.find((p) => p.id === openPayoutId)}
        onClose={() => setOpenPayout(null)}
      />
      <Erp213VoucherSheet
        open={!!openErpId}
        voucherId={openErpId}
        row={erpVouchers.items.find((v) => v.id === openErpId)}
        onClose={() => setOpenErpId(null)}
      />
    </div>
  );
}

const listCard = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', marginTop: -4 };
const exportBtn = { height: 36, padding: '0 12px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', color: 'var(--muted)', fontWeight: 600, fontSize: 11.5, cursor: 'pointer' };
const heroLabel = { fontSize: 11.5, opacity: 0.72, fontWeight: 600, letterSpacing: '.06em' };
// nowrap so "RM" can never orphan onto its own line; tabular-nums so the two
// columns' digits line up with each other.
const heroValue = { fontSize: 24, fontWeight: 700, marginTop: 4, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
